import { IllegalActionError } from './errors';
import type { HandEvaluator } from './ports';
import { isActionLegal } from './rules';
import type {
  Action,
  Card,
  HandResults,
  HandState,
  PayoutEntry,
  Player,
  PlayerId,
  Pot,
} from './types';

/**
 * Apply a single action to hand state. Updates the acting player, betting
 * counters, and action history. Does NOT compute the next pendingActor —
 * that belongs to the reducer (it needs street-advance knowledge).
 *
 * Throws IllegalActionError when the action is not currently legal.
 */
export function applyAction(state: HandState, action: Action): HandState {
  const verdict = isActionLegal(state, action);
  if (!verdict.ok) {
    throw new IllegalActionError({
      playerId: action.playerId,
      attempted: action,
      legalActions: [],
      reason: verdict.reason,
    });
  }

  const actingIdx = state.players.findIndex((p) => p.id === action.playerId);
  if (actingIdx < 0) {
    throw new IllegalActionError({
      playerId: action.playerId,
      attempted: action,
      legalActions: [],
      reason: 'unknown player',
    });
  }
  const current = state.players[actingIdx] as Player;
  const updatedPlayer: Player = { ...current };
  const updatedBetting = { ...state.betting };

  switch (action.type) {
    case 'fold':
      updatedPlayer.folded = true;
      break;
    case 'check':
      break;
    case 'call': {
      const amount = action.amount;
      updatedPlayer.stack -= amount;
      updatedPlayer.streetContribution += amount;
      updatedPlayer.handContribution += amount;
      if (updatedPlayer.stack === 0) updatedPlayer.allIn = true;
      break;
    }
    case 'bet': {
      const amount = action.amount;
      updatedPlayer.stack -= amount;
      updatedPlayer.streetContribution += amount;
      updatedPlayer.handContribution += amount;
      updatedBetting.currentBet = updatedPlayer.streetContribution;
      updatedBetting.lastRaiseSize = amount;
      updatedBetting.minRaise = amount;
      updatedBetting.lastAggressor = action.playerId;
      if (updatedPlayer.stack === 0) updatedPlayer.allIn = true;
      break;
    }
    case 'raise': {
      const delta = action.to - current.streetContribution;
      updatedPlayer.stack -= delta;
      updatedPlayer.streetContribution = action.to;
      updatedPlayer.handContribution += delta;
      const raiseBy = action.to - state.betting.currentBet;
      updatedBetting.currentBet = action.to;
      if (raiseBy >= state.betting.minRaise) {
        updatedBetting.lastRaiseSize = raiseBy;
        updatedBetting.minRaise = raiseBy;
      }
      updatedBetting.lastAggressor = action.playerId;
      if (updatedPlayer.stack === 0) updatedPlayer.allIn = true;
      break;
    }
    case 'allin': {
      const totalCommit = action.amount;
      const delta = totalCommit - current.streetContribution;
      updatedPlayer.stack = 0;
      updatedPlayer.allIn = true;
      updatedPlayer.streetContribution = totalCommit;
      updatedPlayer.handContribution += delta;
      if (totalCommit > state.betting.currentBet) {
        const raiseBy = totalCommit - state.betting.currentBet;
        updatedBetting.currentBet = totalCommit;
        if (raiseBy >= state.betting.minRaise) {
          updatedBetting.lastRaiseSize = raiseBy;
          updatedBetting.minRaise = raiseBy;
        }
        updatedBetting.lastAggressor = action.playerId;
      }
      break;
    }
  }

  const nextPlayers = [...state.players];
  nextPlayers[actingIdx] = updatedPlayer;

  return {
    ...state,
    players: nextPlayers,
    betting: updatedBetting,
    actionHistory: [...state.actionHistory, action],
  };
}

/**
 * Return chips that were committed beyond what any opponent matched to the
 * highest non-folded contributor. Folded contributions count as "matched" up
 * to what the folder put in — those chips stay in the pot.
 *
 * Example: hero bets 500 all-in, villain calls 100 then folds. Hero gets
 * 400 back; villain's 100 stays in the dead pot that hero wins uncontested.
 *
 * Spec §B6: `totalChips` invariant must hold across fold-to-bet sequences.
 */
export function returnUncalledBets(state: HandState): HandState {
  const nonFolded = state.players.filter((p) => !p.folded);
  if (nonFolded.length === 0) return state;
  const topNonFolded = [...nonFolded].sort(
    (a, b) => b.handContribution - a.handContribution,
  )[0] as Player;
  const maxOther = state.players.reduce(
    (max, p) => (p.id === topNonFolded.id ? max : Math.max(max, p.handContribution)),
    0,
  );
  const uncalled = topNonFolded.handContribution - maxOther;
  if (uncalled <= 0) return state;

  const nextPlayers = state.players.map((p) =>
    p.id === topNonFolded.id
      ? {
          ...p,
          stack: p.stack + uncalled,
          handContribution: p.handContribution - uncalled,
        }
      : p,
  );
  return { ...state, players: nextPlayers };
}

/**
 * Build main and side pots from the per-player handContribution tiers.
 * Folded contributions are absorbed into the lowest pots they reached.
 * Spec §B9–B12 (side-pot correctness) is exercised through the test suite.
 */
export function buildSidePots(players: ReadonlyArray<Player>): Pot[] {
  const entries = players
    .filter((p) => p.handContribution > 0)
    .map((p) => ({
      id: p.id,
      folded: p.folded,
      remaining: p.handContribution,
    }));

  const pots: Pot[] = [];

  while (true) {
    const alive = entries.filter((e) => !e.folded && e.remaining > 0);
    if (alive.length === 0) break;
    const cap = Math.min(...alive.map((e) => e.remaining));

    const contributions: Record<PlayerId, number> = {};
    let amount = 0;
    const eligible: PlayerId[] = [];

    for (const e of entries) {
      if (e.remaining <= 0) continue;
      const contrib = Math.min(e.remaining, cap);
      if (contrib > 0) {
        contributions[e.id] = contrib;
        amount += contrib;
      }
      if (!e.folded && e.remaining >= cap) {
        eligible.push(e.id);
      }
      e.remaining -= contrib;
    }

    if (amount > 0) {
      pots.push({ amount, eligible, contributions });
    }
  }

  const leftoverFolded = entries.filter((e) => e.folded && e.remaining > 0);
  if (leftoverFolded.length > 0 && pots.length > 0) {
    const tail = pots[pots.length - 1] as Pot;
    const mergedContribs: Record<PlayerId, number> = { ...tail.contributions };
    let extra = 0;
    for (const e of leftoverFolded) {
      mergedContribs[e.id] = (mergedContribs[e.id] ?? 0) + e.remaining;
      extra += e.remaining;
    }
    pots[pots.length - 1] = {
      amount: tail.amount + extra,
      eligible: tail.eligible,
      contributions: mergedContribs,
    };
  }

  return pots;
}

/**
 * Odd-chip split rule (D3): awarded to the first eligible seat clockwise
 * from the button. Input order is assumed to be seat order starting at the
 * button-left; caller must supply winners in that order.
 */
function distributePot(
  pot: Pot,
  winners: ReadonlyArray<PlayerId>,
  seatOrder: ReadonlyArray<PlayerId>,
): Record<PlayerId, number> {
  if (winners.length === 0) return {};
  const base = Math.floor(pot.amount / winners.length);
  const remainder = pot.amount - base * winners.length;
  const payouts: Record<PlayerId, number> = {};
  for (const id of winners) payouts[id] = base;
  if (remainder > 0) {
    const orderedWinners = seatOrder.filter((id) => winners.includes(id));
    for (let i = 0; i < remainder; i++) {
      const target = orderedWinners[i % orderedWinners.length] as PlayerId;
      payouts[target] = (payouts[target] ?? 0) + 1;
    }
  }
  return payouts;
}

/**
 * Resolve end-of-hand payouts.
 *
 * - Returns uncalled chips
 * - Builds pots from remaining handContributions
 * - Pot with only one eligible player is awarded uncontested (no evaluator call)
 * - Otherwise evaluator picks winners from eligible set
 * - Odd chips go to the first eligible seat clockwise from the button
 */
export function resolveHand(
  state: HandState,
  evaluator: HandEvaluator,
  board: ReadonlyArray<Card>,
): { state: HandState; results: HandResults } {
  const withReturn = returnUncalledBets(state);
  const pots = buildSidePots(withReturn.players);

  const seatOrderFromButton = [...withReturn.players]
    .sort((a, b) => {
      const aDist = (a.seat - withReturn.button + 6) % 6;
      const bDist = (b.seat - withReturn.button + 6) % 6;
      return aDist - bDist;
    })
    .map((p) => p.id);

  const payouts: PayoutEntry[] = [];
  const payoutMap: Record<PlayerId, number> = {};
  const holeByPlayer: Record<PlayerId, readonly [Card, Card] | undefined> = {};
  for (const p of withReturn.players) {
    holeByPlayer[p.id] = p.holeCards;
  }

  pots.forEach((pot, potIndex) => {
    let potWinners: PlayerId[];
    let rank: { name: string; description: string } | undefined;

    if (pot.eligible.length === 1) {
      potWinners = [pot.eligible[0] as PlayerId];
    } else {
      const contenders = pot.eligible
        .filter((id) => holeByPlayer[id] !== undefined)
        .map((id) => ({ playerId: id, holeCards: holeByPlayer[id] as readonly [Card, Card] }));
      if (contenders.length === 0) {
        potWinners = [...pot.eligible];
      } else if (contenders.length === 1) {
        potWinners = [(contenders[0] as NonNullable<(typeof contenders)[number]>).playerId];
      } else {
        potWinners = [...evaluator.winners(contenders, board)];
        const sample = evaluator.rank([
          ...(contenders[0] as NonNullable<(typeof contenders)[number]>).holeCards,
          ...board,
        ]);
        rank = { name: sample.name, description: sample.description };
      }
    }

    const split = distributePot(pot, potWinners, seatOrderFromButton);
    for (const [id, amount] of Object.entries(split)) {
      payoutMap[id] = (payoutMap[id] ?? 0) + amount;
      payouts.push({
        playerId: id,
        potIndex,
        amount,
        handRank: rank?.name,
        handDescription: rank?.description,
      });
    }
  });

  const nextPlayers = withReturn.players.map((p) => ({
    ...p,
    stack: p.stack + (payoutMap[p.id] ?? 0),
  }));

  const results: HandResults = {
    payouts,
    boardRevealedTo: 'river',
  };

  return {
    state: {
      ...withReturn,
      players: nextPlayers,
      betting: { ...withReturn.betting, pots },
      pendingActor: null,
      results,
    },
    results,
  };
}
